import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { EditActionBar } from '../shared/EditActionBar';
import type {
  Driver05Data,
  IndicatorsDetailEntry,
  SummaryByExcellenceRow,
  SummaryByAwardRow,
  NonAwardedOpRow,
} from '../../../api/apiTypesConductRisk';
import { useConductRiskDriver } from '../../../api/hooks/useConductRiskDriver';
import { formatHighlights } from '../../../utils/Format';

const OUTPUTS = ['Eccellenza di Filiale', 'Dettaglio KPI', 'Copertura EF', 'Focus EO'];

const TIPOLOGIA_LABEL: Record<string, string> = {
  RETAIL:          'Retail',
  EXCLUSIVE:       'Exclusive',
  IMPRESE:         'Imprese',
  REMOTO:          'Remoto',
  DIGITALE_REMOTA: 'Digitale Remota',
  TERZO_SETTORE:   'Terzo Settore',
  AGRIBUSINESS:    'Agribusiness',
};

// Etichette sempre in MAIUSCOLO: sia quelle mappate sia le chiavi non mappate
// che arrivano grezze dal BE, così la resa è uniforme in tutta la vista.
const tipoLabel = (k: string) => (TIPOLOGIA_LABEL[k] ?? k).toUpperCase();
const stripCode = (code: string) => code.replace(/^\d+\s+/, '');

const fmtN     = (n: number) => n.toLocaleString('it-IT');
const fmtPct1  = (n: number) => `${n.toFixed(1)}%`;
const fmtPctR  = (n: number) => `${Math.round(n)}%`;

/* ─── Factory righe vuote (per "Aggiungi nuovo") ─────────── */
const emptyIndicatorRow = (): IndicatorsDetailEntry => ({
  territory_code:         '',
  net_promoter_score:     null,
  credit_excellence:      null,
  service_excellence:     null,
  operational_excellence: null,
});

const emptyExcellenceRow = (): SummaryByExcellenceRow => ({
  segment:           '',
  excellence_ge_100: 0,
  total_branches:    0,
  pct:               0,
});

const emptyAwardRow = (): SummaryByAwardRow => ({
  segment:                  '',
  awarded_branches:         0,
  non_awarded_branches:     0,
  total_excellent_branches: 0,
  awarded_pct:              0,
  non_awarded_pct:          0,
});

const emptyOpFocusRow = (): NonAwardedOpRow => ({
  segment:              '',
  op_excellence_gt_100: 0,
  op_excellence_lt_100: 0,
  total:                0,
  gt_pct:               0,
  lt_pct:               0,
});

/* ─── Helpers ────────────────────────────────────────────── */
const ScoreBar = ({ score, nome }: { score: number; nome: string }) => {
  const ok   = score >= 100;
  const warn = score >= 90;
  const txtColor = ok ? 'text-emerald-600' : warn ? 'text-amber-600' : 'text-red-500';
  const border   = ok ? 'border-emerald-200' : warn ? 'border-amber-200' : 'border-red-200';
  const bg       = ok ? 'bg-emerald-50'      : warn ? 'bg-amber-50'      : 'bg-red-50';
  const bar      = ok ? 'bg-emerald-400'     : warn ? 'bg-amber-400'     : 'bg-red-400';
  const widthPct = Math.min((score / 130) * 100, 100);
  return (
    <div className={`rounded-xl border p-4 ${bg} ${border}`}>
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-xs font-semibold text-[#1B365D]">{nome}</span>
        <span className={`text-2xl font-bold ${txtColor}`}>{score}</span>
      </div>
      <div className="h-2 bg-white/70 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${bar}`} style={{ width: `${widthPct}%` }} />
      </div>
    </div>
  );
};

const numCls = 'w-16 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:border-[#1B365D]/50 tabular-nums';
const txtCls = 'w-full min-w-[6rem] bg-white border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-xs text-[#1B365D] focus:outline-none focus:border-[#1B365D]/50';

/* ─── Main ───────────────────────────────────────────────── */
const FocusKpiEFEOContainer = ({ reportId }: { reportId: string }) => {
  const { data: remoteData, loading, error, save, isReadOnly } = useConductRiskDriver<Driver05Data>(reportId, 5);

  const [data,         setData]         = useState<Driver05Data | null>(null);
  const [savedData,    setSavedData]    = useState<Driver05Data | null>(null);
  const [isEditing,    setIsEditing]    = useState(false);
  const [activeOutput, setActiveOutput] = useState(0);

  useEffect(() => {
    if (remoteData) {
      setData(remoteData);
      setSavedData(remoteData);
    }
  }, [remoteData]);

  const handleAnnulla = () => { if (savedData) setData(savedData); setIsEditing(false); };
  const handleSalva   = async () => {
    if (!data) return;
    await save(data);
    setSavedData(data);
    setIsEditing(false);
  };

  const updateOverallScore = (i: number, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      overall_score: prev.overall_score.map((r, ri) => ri === i ? { ...r, score: val } : r),
    } : prev));
  };

  const updateIndicator = (i: number, field: keyof IndicatorsDetailEntry, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      indicators_detail: prev.indicators_detail.map((r, ri) => ri === i ? { ...r, [field]: val } : r),
    } : prev));
  };

  const updateExcellenceRow = (i: number, field: keyof SummaryByExcellenceRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      summary_by_excellence: {
        ...prev.summary_by_excellence,
        rows: prev.summary_by_excellence.rows.map((r, ri) => ri === i ? { ...r, [field]: val } : r),
      },
    } : prev));
  };

  const updateAwardRow = (i: number, field: keyof SummaryByAwardRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      summary_by_award: {
        ...prev.summary_by_award,
        rows: prev.summary_by_award.rows.map((r, ri) => ri === i ? { ...r, [field]: val } : r),
      },
    } : prev));
  };

  const updateOpFocusRow = (i: number, field: keyof NonAwardedOpRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      non_awarded_operational_focus: {
        ...prev.non_awarded_operational_focus,
        rows: prev.non_awarded_operational_focus.rows.map((r, ri) => ri === i ? { ...r, [field]: val } : r),
      },
    } : prev));
  };

  /* ─── Aggiunta righe ("Aggiungi nuovo") ─── */
  const addIndicator = () => {
    setData(prev => (prev ? {
      ...prev,
      indicators_detail: [...prev.indicators_detail, emptyIndicatorRow()],
    } : prev));
  };

  const addExcellenceRow = () => {
    setData(prev => (prev ? {
      ...prev,
      summary_by_excellence: {
        ...prev.summary_by_excellence,
        rows: [...prev.summary_by_excellence.rows, emptyExcellenceRow()],
      },
    } : prev));
  };

  const addAwardRow = () => {
    setData(prev => (prev ? {
      ...prev,
      summary_by_award: {
        ...prev.summary_by_award,
        rows: [...prev.summary_by_award.rows, emptyAwardRow()],
      },
    } : prev));
  };

  const addOpFocusRow = () => {
    setData(prev => (prev ? {
      ...prev,
      non_awarded_operational_focus: {
        ...prev.non_awarded_operational_focus,
        rows: [...prev.non_awarded_operational_focus.rows, emptyOpFocusRow()],
      },
    } : prev));
  };

  /* ─── Updater testuali (colonne nome/territorio, valore grezzo) ─── */
  const updateOverallScoreCode = (i: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      overall_score: prev.overall_score.map((r, ri) => ri === i ? { ...r, territory_code: value } : r),
    } : prev));
  };

  const updateIndicatorCode = (i: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      indicators_detail: prev.indicators_detail.map((r, ri) => ri === i ? { ...r, territory_code: value } : r),
    } : prev));
  };

  const updateExcellenceRowName = (i: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      summary_by_excellence: {
        ...prev.summary_by_excellence,
        rows: prev.summary_by_excellence.rows.map((r, ri) => ri === i ? { ...r, segment: value } : r),
      },
    } : prev));
  };

  const updateAwardRowName = (i: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      summary_by_award: {
        ...prev.summary_by_award,
        rows: prev.summary_by_award.rows.map((r, ri) => ri === i ? { ...r, segment: value } : r),
      },
    } : prev));
  };

  const updateOpFocusRowName = (i: number, value: string) => {
    setData(prev => (prev ? {
      ...prev,
      non_awarded_operational_focus: {
        ...prev.non_awarded_operational_focus,
        rows: prev.non_awarded_operational_focus.rows.map((r, ri) => ri === i ? { ...r, segment: value } : r),
      },
    } : prev));
  };

  /* ─── Updater numerici dei totali (tfoot) ─── */
  const updateExcellenceTotals = (field: keyof SummaryByExcellenceRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      summary_by_excellence: { ...prev.summary_by_excellence, totals: { ...prev.summary_by_excellence.totals, [field]: val } },
    } : prev));
  };

  const updateAwardTotals = (field: keyof SummaryByAwardRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      summary_by_award: { ...prev.summary_by_award, totals: { ...prev.summary_by_award.totals, [field]: val } },
    } : prev));
  };

  const updateOpFocusTotals = (field: keyof NonAwardedOpRow, raw: string) => {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setData(prev => (prev ? {
      ...prev,
      non_awarded_operational_focus: { ...prev.non_awarded_operational_focus, totals: { ...prev.non_awarded_operational_focus.totals, [field]: val } },
    } : prev));
  };

  const updateHighlights = (field: 'excellence_highlights' | 'non_awarded_operational_highlights', value: string) => {
    setData(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-12 h-12 border-[5px] border-gray-200 border-t-[#1B365D] rounded-full animate-spin" />
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-gray-400">Nessun dato disponibile per questo driver.</div>;
  }

  const exc       = data.summary_by_excellence;
  const award     = data.summary_by_award;
  const opFocus   = data.non_awarded_operational_focus;

  const currentHighlightField: 'excellence_highlights' | 'non_awarded_operational_highlights' =
    activeOutput === 3 ? 'non_awarded_operational_highlights' : 'excellence_highlights';
  const currentHighlight = data[currentHighlightField];

  return (
    <div className="p-6 space-y-5">

      {/* Output tabs + edit bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 p-1 bg-gray-100 rounded-xl flex-1 min-w-0">
          {OUTPUTS.map((label, i) => (
            <button
              key={i}
              onClick={() => setActiveOutput(i)}
              className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                activeOutput === i
                  ? 'bg-white text-[#1B365D] shadow-sm'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <EditActionBar isEditing={isEditing} onProcedi={() => setIsEditing(true)} onAnnulla={handleAnnulla} onSalva={handleSalva} disabled={isReadOnly} />
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-2xl font-bold text-[#1B365D]">{fmtN(exc.totals.total_branches)}</div>
          <div className="text-[10px] text-gray-400 mt-1">Filiali in scope</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600">{fmtN(exc.totals.excellence_ge_100)}</div>
          <div className="text-[10px] text-gray-400 mt-1">EF ≥ 100 ({fmtPct1(exc.totals.pct)})</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-2xl font-bold text-[#1B365D]">{fmtN(award.totals.awarded_branches)}</div>
          <div className="text-[10px] text-gray-400 mt-1">Filiali a premio ({fmtPct1(award.totals.awarded_pct)})</div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{fmtN(award.totals.non_awarded_branches)}</div>
          <div className="text-[10px] text-gray-400 mt-1">Filiali non a premio ({fmtPct1(award.totals.non_awarded_pct)})</div>
        </div>
      </div>

      {/* Highlights */}
      <div className="px-4 py-3 rounded-xl bg-[#1B365D]/5 border border-[#1B365D]/10 text-xs text-[#1B365D]/80 leading-relaxed">
        <span className="font-semibold">Highlights: </span>
        {isEditing
          ? <textarea
              className="w-full mt-1 p-2 text-xs text-[#1B365D]/80 leading-relaxed border border-[#1B365D]/20 rounded-lg resize-y focus:outline-none focus:border-[#1B365D]/40 bg-white/70 min-h-[100px]"
              value={currentHighlight}
              onChange={e => updateHighlights(currentHighlightField, e.target.value)}
            />
          : <span className="whitespace-pre-wrap">{formatHighlights(currentHighlight)}</span>}
      </div>

      {/* Output content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeOutput}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
        >

          {/* OUTPUT 1 — Score complessivo per territorio */}
          {activeOutput === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.overall_score.map((row, i) => (
                isEditing ? (
                  <div key={i} className="rounded-xl border border-gray-100 bg-white p-4 flex items-center justify-between gap-3">
                    <input
                      type="text"
                      className={txtCls}
                      value={row.territory_code}
                      onChange={e => updateOverallScoreCode(i, e.target.value)}
                    />
                    <input
                      type="number"
                      step="0.1"
                      className="w-20 text-right bg-white text-[#1B365D] border border-[#1B365D]/20 rounded px-1.5 py-0.5 text-sm font-bold focus:outline-none focus:border-[#1B365D]/50 tabular-nums"
                      value={row.score}
                      onChange={e => updateOverallScore(i, e.target.value)}
                    />
                  </div>
                ) : (
                  <ScoreBar key={i} score={row.score} nome={stripCode(row.territory_code)} />
                )
              ))}
            </div>
          )}

          {/* OUTPUT 2 — Dettaglio KPI */}
          {activeOutput === 1 && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100">
                <p className="text-xs font-semibold text-[#1B365D]">Dettaglio indicatori KPI per territorio — {data.period}</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                      <th className="text-left  px-5 py-2.5 font-semibold">Territorio</th>
                      <th className="text-right px-4 py-2.5 font-semibold">NPS</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Eccellenza Credito</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Eccellenza Servizio</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Eccellenza Operativa</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {data.indicators_detail.map((t, i) => (
                      <motion.tr
                        key={i}
                        initial={{ opacity: 0, x: -4 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.04 }}
                        className="hover:bg-gray-50/60 transition-colors"
                      >
                        <td className="px-5 py-3 font-medium text-[#1B365D]">{isEditing ? <input type="text" className={txtCls} value={t.territory_code} onChange={e => updateIndicatorCode(i, e.target.value)} /> : stripCode(t.territory_code)}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">
                          {isEditing
                            ? <input type="number" step="0.1" className={numCls} value={t.net_promoter_score ?? ''} onChange={e => updateIndicator(i, 'net_promoter_score', e.target.value)} />
                            : (t.net_promoter_score ?? '—')}
                        </td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{isEditing ? <input type="number" step="0.1" className={numCls} value={t.credit_excellence ?? ''} onChange={e => updateIndicator(i, 'credit_excellence', e.target.value)} /> : (t.credit_excellence ?? '—')}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{isEditing ? <input type="number" step="0.1" className={numCls} value={t.service_excellence ?? ''} onChange={e => updateIndicator(i, 'service_excellence', e.target.value)} /> : (t.service_excellence ?? '—')}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{isEditing ? <input type="number" step="0.1" className={numCls} value={t.operational_excellence ?? ''} onChange={e => updateIndicator(i, 'operational_excellence', e.target.value)} /> : (t.operational_excellence ?? '—')}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => addIndicator()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
            </div>
          )}

          {/* OUTPUT 3 — Copertura EF (2 tabelle) */}
          {activeOutput === 2 && (
            <div className="space-y-5">
              {/* Tabella 1: EF ≥ 100 / Totale Filiali */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-[#1B365D]">Eccellenza di Filiale — {data.period}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                        <th className="text-left  px-5 py-2.5 font-semibold">Territorio</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-emerald-600">Eccellenza di Filiale ≥ 100</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-emerald-600">%</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Totale Filiali</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {exc.rows.map((r, i) => (
                        <motion.tr key={i} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-5 py-3 font-medium text-[#1B365D]">{isEditing ? <input type="text" className={txtCls} value={r.segment} onChange={e => updateExcellenceRowName(i, e.target.value)} /> : tipoLabel(r.segment)}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-emerald-600">
                            {isEditing ? <input type="number" className={numCls} value={r.excellence_ge_100} onChange={e => updateExcellenceRow(i, 'excellence_ge_100', e.target.value)} /> : fmtN(r.excellence_ge_100)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isEditing
                              ? <input type="number" className={numCls} value={r.pct} onChange={e => updateExcellenceRow(i, 'pct', e.target.value)} />
                              : <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${r.pct >= 95 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : r.pct >= 85 ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-red-50 text-red-500 border-red-200'}`}>{fmtPct1(r.pct)}</span>}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-600">
                            {isEditing ? <input type="number" className={numCls} value={r.total_branches} onChange={e => updateExcellenceRow(i, 'total_branches', e.target.value)} /> : fmtN(r.total_branches)}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
                        <td className="px-5 py-3">Totale</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={exc.totals.excellence_ge_100} onChange={e => updateExcellenceTotals('excellence_ge_100', e.target.value)} /> : fmtN(exc.totals.excellence_ge_100)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={exc.totals.pct} onChange={e => updateExcellenceTotals('pct', e.target.value)} /> : fmtPct1(exc.totals.pct)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={exc.totals.total_branches} onChange={e => updateExcellenceTotals('total_branches', e.target.value)} /> : fmtN(exc.totals.total_branches)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => addExcellenceRow()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}

              {/* Tabella 2: A Premio / Non a Premio (base = filiali con EF ≥ 100) */}
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-[#1B365D]">Filiali a Premio / Non a Premio — {data.period}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">Base: filiali con Eccellenza di Filiale ≥ 100</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                        <th className="text-left  px-5 py-2.5 font-semibold">Territorio</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Filiali a Premio</th>
                        <th className="text-right px-4 py-2.5 font-semibold">%</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-amber-500">Filiali non a Premio</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-amber-500">%</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Totale (EF ≥ 100)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {award.rows.map((r, i) => (
                        <motion.tr key={i} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-5 py-3 font-medium text-[#1B365D]">{isEditing ? <input type="text" className={txtCls} value={r.segment} onChange={e => updateAwardRowName(i, e.target.value)} /> : tipoLabel(r.segment)}</td>
                          <td className="px-4 py-2.5 text-right text-gray-600">
                            {isEditing ? <input type="number" className={numCls} value={r.awarded_branches} onChange={e => updateAwardRow(i, 'awarded_branches', e.target.value)} /> : fmtN(r.awarded_branches)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500">{isEditing ? <input type="number" className={numCls} value={r.awarded_pct} onChange={e => updateAwardRow(i, 'awarded_pct', e.target.value)} /> : fmtPctR(r.awarded_pct)}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-amber-600">
                            {isEditing ? <input type="number" className={numCls} value={r.non_awarded_branches} onChange={e => updateAwardRow(i, 'non_awarded_branches', e.target.value)} /> : fmtN(r.non_awarded_branches)}
                          </td>
                          <td className="px-4 py-3 text-right text-amber-500">{isEditing ? <input type="number" className={numCls} value={r.non_awarded_pct} onChange={e => updateAwardRow(i, 'non_awarded_pct', e.target.value)} /> : fmtPctR(r.non_awarded_pct)}</td>
                          <td className="px-4 py-3 text-right text-gray-600">{isEditing ? <input type="number" className={numCls} value={r.total_excellent_branches} onChange={e => updateAwardRow(i, 'total_excellent_branches', e.target.value)} /> : fmtN(r.total_excellent_branches)}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
                        <td className="px-5 py-3">Totale</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={award.totals.awarded_branches} onChange={e => updateAwardTotals('awarded_branches', e.target.value)} /> : fmtN(award.totals.awarded_branches)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={award.totals.awarded_pct} onChange={e => updateAwardTotals('awarded_pct', e.target.value)} /> : fmtPctR(award.totals.awarded_pct)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={award.totals.non_awarded_branches} onChange={e => updateAwardTotals('non_awarded_branches', e.target.value)} /> : fmtN(award.totals.non_awarded_branches)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={award.totals.non_awarded_pct} onChange={e => updateAwardTotals('non_awarded_pct', e.target.value)} /> : fmtPctR(award.totals.non_awarded_pct)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={award.totals.total_excellent_branches} onChange={e => updateAwardTotals('total_excellent_branches', e.target.value)} /> : fmtN(award.totals.total_excellent_branches)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => addAwardRow()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
            </div>
          )}

          {/* OUTPUT 4 — Focus Eccellenza Operativa */}
          {activeOutput === 3 && (
            <div className="space-y-4">
              <div className="px-4 py-2.5 bg-amber-50 border border-amber-100 rounded-xl">
                <p className="text-xs text-amber-700">
                  Analisi dell&apos;Eccellenza Operativa sulle sole filiali <b>non a premio</b> — le filiali con
                  EO&nbsp;&lt;&nbsp;100 sono potenziali anomalie da approfondire.
                </p>
              </div>
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-[#1B365D]">Focus Eccellenza Operativa — filiali non a premio — {data.period}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-400 uppercase text-[8px] tracking-wide">
                        <th className="text-left  px-5 py-2.5 font-semibold">Territorio</th>
                        <th className="text-right px-4 py-2.5 font-semibold">Non a premio</th>
                        <th className="text-right px-4 py-2.5 font-semibold">EO &gt; 100</th>
                        <th className="text-right px-4 py-2.5 font-semibold">% EO &gt; 100</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-amber-500">EO &lt; 100</th>
                        <th className="text-right px-4 py-2.5 font-semibold text-amber-500">% EO &lt; 100</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {opFocus.rows.map((r, i) => (
                        <motion.tr key={i} initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-5 py-3 font-medium text-[#1B365D]">{isEditing ? <input type="text" className={txtCls} value={r.segment} onChange={e => updateOpFocusRowName(i, e.target.value)} /> : tipoLabel(r.segment)}</td>
                          <td className="px-4 py-3 text-right text-gray-600">{isEditing ? <input type="number" className={numCls} value={r.total} onChange={e => updateOpFocusRow(i, 'total', e.target.value)} /> : fmtN(r.total)}</td>
                          <td className="px-4 py-2.5 text-right text-gray-600">
                            {isEditing ? <input type="number" className={numCls} value={r.op_excellence_gt_100} onChange={e => updateOpFocusRow(i, 'op_excellence_gt_100', e.target.value)} /> : fmtN(r.op_excellence_gt_100)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-500">{isEditing ? <input type="number" className={numCls} value={r.gt_pct} onChange={e => updateOpFocusRow(i, 'gt_pct', e.target.value)} /> : fmtPct1(r.gt_pct)}</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-amber-600">
                            {isEditing ? <input type="number" className={numCls} value={r.op_excellence_lt_100} onChange={e => updateOpFocusRow(i, 'op_excellence_lt_100', e.target.value)} /> : fmtN(r.op_excellence_lt_100)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {isEditing
                              ? <input type="number" className={numCls} value={r.lt_pct} onChange={e => updateOpFocusRow(i, 'lt_pct', e.target.value)} />
                              : <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${r.lt_pct >= 5 ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-gray-50 text-gray-500 border-gray-200'}`}>{fmtPct1(r.lt_pct)}</span>}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-[#1B365D] text-white text-[11px] font-semibold">
                        <td className="px-5 py-3">Totale</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={opFocus.totals.total} onChange={e => updateOpFocusTotals('total', e.target.value)} /> : fmtN(opFocus.totals.total)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={opFocus.totals.op_excellence_gt_100} onChange={e => updateOpFocusTotals('op_excellence_gt_100', e.target.value)} /> : fmtN(opFocus.totals.op_excellence_gt_100)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={opFocus.totals.gt_pct} onChange={e => updateOpFocusTotals('gt_pct', e.target.value)} /> : fmtPct1(opFocus.totals.gt_pct)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={opFocus.totals.op_excellence_lt_100} onChange={e => updateOpFocusTotals('op_excellence_lt_100', e.target.value)} /> : fmtN(opFocus.totals.op_excellence_lt_100)}</td>
                        <td className="px-4 py-3 text-right">{isEditing ? <input type="number" className={numCls} value={opFocus.totals.lt_pct} onChange={e => updateOpFocusTotals('lt_pct', e.target.value)} /> : fmtPct1(opFocus.totals.lt_pct)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              {isEditing && (
                <div className="flex justify-end mt-2">
                  <button
                    type="button"
                    onClick={() => addOpFocusRow()}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1B365D] border border-[#1B365D]/30 hover:bg-[#1B365D]/5 rounded-lg px-3 py-1.5 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Aggiungi nuovo
                  </button>
                </div>
              )}
            </div>
          )}

        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default FocusKpiEFEOContainer;
